// ─── Turn markers: the one place that understands the AI's inline grammar ───
//
// The troubleshoot AI is taught (in worker/routes/troubleshoot.js SYSTEM_PROMPT)
// to embed structured markers in its replies:
//
//   [doc:42]                              → reference to document 42
//   [doc:42 page:5]                       → page 5 of document 42
//   [svg ...]<svg>…</svg>[/svg]           → an inline schematic it drew
//   [expect unit=ohms min=10 max=40]      → the reading it expects back
//
// Markers are for the app, never for the tech: every surface that shows a turn
// to a human strips them, and the worker gates the [expect] one before storage.
// That logic lived in three places and agreed in none of them, so it lives here.
//
// ⚠️ This module is imported by BOTH bundles — the Worker (esbuild, via
// wrangler) and the frontend (vite). Both inline a relative import across
// directories; this was measured, not assumed. Keep it dependency-free: no
// React, no DOM, no `env`, no Node built-ins. It must stay runnable under
// `node --test` with no loader.
//
// ⚠️ Never write a regex word boundary as anything but the two characters
// backslash + b. A literal 0x08 byte renders identically in most editors and
// silently makes the pattern unsatisfiable — that is exactly how the [expect]
// guard shipped dead on 2026-09-06 (f709d36) and took the whole structured-
// reading feature with it. `npm test` and CI both scan for control bytes now.

// ─── Grammar ────────────────────────────────────────────────────────────────

// Every well-formed marker. Ordered alternation: doc, svg, expect.
//
// ⚠️ The expect alternative is bounded to ONE LINE ([^\]\n]*). With [^\]]* it
// spans newlines, so an unterminated marker followed by a well-formed one on
// the next line parses as a SINGLE marker whose attributes are a merge of both
// specs — an ohms floor welded to a temperature value. Both ingredients occur
// in production (2 of 8 markers arrived unterminated; turn 544 emitted two).
export const MARKER_RE =
  /(\[doc:\s*(\d+)(?:\s*[, ]\s*page:\s*(\d+))?\s*\])|(\[svg(?:\s+[^\]]*)?\]([\s\S]*?)\[\/svg\])|(\[expect\s+([^\]\n]*)\])/g;

// A marker the model started and never closed. MARKER_RE requires a closing
// bracket in every alternative, so without this sweep an unterminated marker
// is neither captured nor stripped and falls through into the prose the tech
// reads — and that the read-aloud button speaks. Production turns 474 and 476
// are in exactly this state. Anchored to end-of-line (`m`) so it cannot eat a
// bracketed part number mid-sentence ("the [red] wire", "part [WR55X]").
export const UNTERMINATED_RE = /\[(?:expect\b|svg\b|doc:)[^\]\n]*$/gm;

// Units the expected-reading marker and the reading composer share.
export const READING_UNITS = {
  ohms: { label: 'Ω',   name: 'ohms' },
  // Thermistors are tabulated in kilohms and meters display "13.29 kΩ".
  kohm: { label: 'kΩ',  name: 'kilohms' },
  vac:  { label: 'VAC', name: 'volts AC' },
  vdc:  { label: 'VDC', name: 'volts DC' },
  amps: { label: 'A',   name: 'amps' },
  f:    { label: '°F',  name: 'degrees F' },
  c:    { label: '°C',  name: 'degrees C' },
  uf:   { label: 'µF',  name: 'microfarads' },
  // Gas pressure at the burner, read on a manometer (D-SCRIPT14).
  inwc: { label: 'in. WC', name: 'inches of water column' },
};

// Does this prose describe meter or thermometer work? Decides whether an
// [expect …] marker is legitimate on the turn (see applyExpectGuard).
//
// Three things here are load-bearing and were each measured against the 298
// AI turns in production:
//   • Plurals. "reading" cannot match "readings", and a real production
//     measurement turn (544, "Take IR readings at three levels…") was being
//     stripped by the obvious `\b`-only repair because of it.
//   • The bare symbols Ω ° µF sit OUTSIDE the \b group. `\b` before Ω needs an
//     adjacent word character, so "you should see 10 Ω there" misses it; and
//     a `(?![a-z])` guard under /i rejects the F in "37°F".
//   • µF is spelled out rather than a bare `F\b`, which would match "of",
//     "off", "if", "self", "half", "proof" and effectively disable the guard.
// Keep it BIAS-TO-KEEP. A stray pill on a yes/no turn is a shrug; a wrongly
// stripped marker deletes the feature for that turn — and the worker rebuilds
// the model's context from stored turns, so it also erases the model's own
// example of correct behaviour.
export const MEASURES =
  /\b(?:ohms?|volts?|voltage|vac|vdc|amps?|amperage|resistance|continuity|multimeter|meters?|readings?|degrees?|temps?|temperature|microfarads?|thermometer|measure[sd]?|measurement|kilohms?|pressure|water column|manometer|wc)\b|[Ω°]|µ\s?f/i;

const EXPECT_ANY_RE = /\[expect[^\]]*\]/gi;

// ─── Worker side ────────────────────────────────────────────────────────────

/**
 * Gate the [expect …] marker on an AI reply before it is stored.
 *
 * Three passes, in this order:
 *   (a) repair a trailing marker whose closing bracket the model dropped,
 *   (b) strip it when the prose describes no measurement at all,
 *   (c) strip ALL of them when the model emitted more than one.
 *
 * (c) is not fussiness. The grammar says exactly one marker per turn, and a
 * turn asking for six temperatures across two compartments has no single
 * range that can judge the answer — production turn 544 emitted a fresh-food
 * range and a freezer range together. Keeping either one makes a correct
 * reading report FAIL, and that fabricated verdict is prepended to the tech's
 * answer and fed to the model. No reading row is the honest outcome.
 */
export function applyExpectGuard(input) {
  let text = String(input || '');

  // (a) Bounded to key=value tokens, and it must find at least ONE of them:
  //     with `*` a reply ending in a bare "[expect" gets a closing bracket
  //     manufactured for it, and the resulting literal "[expect]" is invisible
  //     to every later filter — (b) needs `\[expect\s`, MARKER_RE needs
  //     `\[expect\s+`, UNTERMINATED_RE needs the absence of `]` — so it lands
  //     in the prose the tech reads and the read-aloud speaks.
  //
  //     The lookahead closes a marker mid-line as well as at the end. Closing
  //     only trailing markers left the two halves of this module disagreeing
  //     about the same input: the guard kept "…[expect unit=ohms value=0 and
  //     tell me what you read." intact for the model, while the render sweep
  //     below ate to end of line and showed the tech a turn with the question
  //     removed. The lookahead fails when a `]` already follows on the line,
  //     so a well-formed marker is never touched.
  text = text.replace(/\[expect((?:\s+\w+=[^\s\]]+)+)(?![^\n\]]*\])/gi, '[expect$1]');

  // (b) Cheap presence test first: MEASURES then runs on the ~3% of turns
  //     that carry a marker rather than on every reply.
  if (/\[expect\s/i.test(text) && !MEASURES.test(text.replace(EXPECT_ANY_RE, ''))) {
    text = text.replace(/\s*\[expect[^\]]*\]\s*/gi, ' ').trim();
  }

  // (c) More than one is a grammar violation; neither can be trusted.
  //
  //     Counted on the OPENING token, not on whole markers. EXPECT_ANY_RE's
  //     `[^\]]*` runs across newlines, so an unterminated marker followed by a
  //     well-formed one matched as ONE marker, this test never fired, and the
  //     merged span reached the client as a single pill built from two specs.
  if ((text.match(/\[expect\b/gi) || []).length > 1) {
    text = text.replace(/\s*\[expect[^\]]*\]\s*/gi, ' ').trim();
  }

  return text;
}

// ─── Expected-reading marker ────────────────────────────────────────────────

/**
 * "[expect unit=ohms min=10 max=40]" → { unit, min, max, value, tol }
 * (or { unit, ol: true }). Null when the marker is malformed — the prose
 * still stands on its own.
 */
export function parseExpect(attrs) {
  const kv = {};
  for (const m of String(attrs || '').matchAll(/(\w+)\s*=\s*([^\s\]]+)/g)) kv[m[1].toLowerCase()] = m[2];
  const unit = (kv.unit || '').toLowerCase();
  // hasOwnProperty, not truthiness: a bare `READING_UNITS[unit]` test lets
  // "constructor" and "__proto__" through and renders a pill of "10–40 undefined".
  if (!Object.prototype.hasOwnProperty.call(READING_UNITS, unit)) return null;
  // A bound that doesn't parse becomes null, never NaN. NaN survives the
  // all-null check below, then loses every comparison in judgeReading — so
  // "min=abc max=40" used to report a confident FAIL on a healthy reading.
  // A trailing % is tolerated because the model writes tol=5% despite the
  // grammar; dropping the tolerance entirely would silently widen the check.
  const num = (v) => {
    if (v == null || v === '') return null;
    const n = Number(String(v).replace(/%\s*$/, ''));
    return Number.isFinite(n) ? n : null;
  };
  // Where the value came from, as tagged by the worker (tagExpectSource).
  const src = kv.src === 'manual' || kv.src === 'typical' ? kv.src : undefined;
  if (/^ol$/i.test(kv.value || '')) return src ? { unit, ol: true, src } : { unit, ol: true };
  const out = { unit, min: num(kv.min), max: num(kv.max), value: num(kv.value), tol: num(kv.tol) };
  if ([out.min, out.max, out.value].every((v) => v == null)) return null;
  if (src) out.src = src;
  return out;
}

// The tolerance judgeReading will actually apply to a `value=` target. A
// marker with no tol is judged against ±10%, and a zero target against an
// absolute ±2 — neither of which used to appear anywhere the tech could see.
// The real production marker [expect unit=f value=37] therefore rendered a
// pill reading "37 °F" while quietly passing 40.5 °F, which is the complaint
// in a fresh-food compartment, not the pass condition. The pill and the judge
// must not be able to disagree, so both read this.
/**
 * Tag the reply's [expect …] marker with where its value came from (D-REV16):
 * src=manual when every number in it appears in `sources` (the manual pages
 * and document facts the AI was given this turn), else src=typical. The pill
 * shows the difference, because a general-knowledge number otherwise looks
 * exactly like a manufacturer spec and drives PASS/FAIL. A src the model
 * wrote itself is discarded. OL is never claimed for the manual.
 */
export function tagExpectSource(text, sources = []) {
  const input = String(text || '');
  const m = /\[expect\s+([^\]\n]*)\]/i.exec(input);
  if (!m) return input;
  const parsed = parseExpect(m[1]);
  if (!parsed) return input;
  const nums = parsed.ol ? [] : [parsed.min, parsed.max, parsed.value].filter((v) => v != null);
  const haystack = (sources || []).filter(Boolean).map(String);
  const fromManual = nums.length > 0 && nums.every((n) => haystack.some((h) => numberAppears(h, n)));
  const attrs = m[1].replace(/\s*\bsrc\s*=\s*[^\s\]]+/gi, '').trim();
  const tagged = `[expect ${attrs} src=${fromManual ? 'manual' : 'typical'}]`;
  return input.slice(0, m.index) + tagged + input.slice(m.index + m[0].length);
}

// True when `n` is written in `text` as a number of its own: "20" matches
// "20 ohms" and "20Ω", not "120" or "20.5".
function numberAppears(text, n) {
  const literal = String(n).replace(/[.]/g, '\\.').replace(/^-/, '-?');
  return new RegExp(`(?<![\\d.])${literal}(?!\\d|\\.\\d)`).test(text);
}

/** The pill's lead-in: whether the tech is looking at a spec or a typical value. */
export function expectLabel(e) {
  if (e?.src === 'manual') return 'Expected (manual)';
  if (e?.src === 'typical') return 'Typical value — not from this manual';
  return 'Expected';
}

const DEFAULT_TOL_PCT = 10;
const ZERO_TARGET_ABS = 2;
function effectiveTol(e) {
  return e.tol != null ? e.tol : DEFAULT_TOL_PCT;
}

export function describeExpect(e) {
  if (!e) return '';
  // Optional-chained: this runs on a render path, and a throw here blanks the
  // whole transcript rather than one pill.
  const u = READING_UNITS[e.unit]?.label || e.unit || '';
  if (e.ol) return `OL (open) on ${u}`;
  if (e.min != null && e.max != null) return `${e.min}–${e.max} ${u}`;
  if (e.min != null) return `≥ ${e.min} ${u}`;
  if (e.max != null) return `≤ ${e.max} ${u}`;
  if (e.value === 0) return `0 ${u} ±${ZERO_TARGET_ABS}`;
  if (e.value != null) return `${e.value} ${u} ±${effectiveTol(e)}%`;
  return '';
}

// Two pairs convert: °F/°C, and Ω/kΩ (one quantity at two scales). Everything
// else measures a different physical quantity — a "conversion" between them is
// a category error, and guessing one would be the fabricated verdict this
// module exists to prevent.
export const CONVERTIBLE = { f: 'c', c: 'f', ohms: 'kohm', kohm: 'ohms' };

function convertReading(v, from, to) {
  if (from === to) return v;
  if (from === 'c' && to === 'f') return v * 9 / 5 + 32;
  if (from === 'f' && to === 'c') return (v - 32) * 5 / 9;
  // Rounded to 12 significant digits: 16.1 × 1000 is 16100.000000000002 in
  // floating point, which failed a reading sitting exactly on a 16100 Ω limit.
  if (from === 'kohm' && to === 'ohms') return Number((v * 1000).toPrecision(12));
  if (from === 'ohms' && to === 'kohm') return Number((v / 1000).toPrecision(12));
  return null;
}

/**
 * PASS / FAIL for a reading against the marker.
 *
 *   e           the parsed [expect …]
 *   raw         what the tech typed, or "OL"
 *   readingUnit which unit chip the tech has selected — SEPARATE state from
 *               e.unit, and the reason this function takes a third argument.
 *               Without it, 20 on the VAC chip judged PASS against a 10–40 Ω
 *               spec and asserted "Reading: 20 VAC — PASS" to the model.
 *
 * Returns { verdict, text } where verdict is
 * 'pass' | 'fail' | 'ol' | 'mismatch' | null. Null means "say nothing" — the
 * correct answer for anything we cannot judge honestly.
 */
export function judgeReading(e, raw, readingUnit) {
  if (!e) return { verdict: null, text: '' };
  const isOl = /^ol$/i.test(String(raw).trim());

  // The unit check runs BEFORE the OL branches and before the numeric parse.
  // It used to sit below them, so an OL tapped with the wrong unit chip
  // selected skipped it entirely and produced a confident verdict — "Reading:
  // OL °F — FAIL (OL — open circuit) (expected 10–40 Ω)" — which contradicts
  // itself in one line and is asserted to the model. OL is the reading techs
  // enter most, so this is the likeliest way to reach a fabricated verdict.
  //
  // Only the non-convertible case can be settled here; an f<->c reading still
  // needs the numeric conversion below, which cannot run until `v` exists.
  const declared = readingUnit || e.unit;
  if (declared !== e.unit && CONVERTIBLE[e.unit] !== declared) {
    const a = READING_UNITS[declared]?.label || declared;
    const b = READING_UNITS[e.unit]?.label || e.unit;
    return { verdict: 'mismatch', text: `MISMATCH (${a} vs ${b})` };
  }

  if (e.ol) {
    return isOl
      ? { verdict: 'pass', text: 'PASS (open, as expected)' }
      : { verdict: 'fail', text: 'FAIL (not open — expected OL)' };
  }
  if (isOl) {
    // OL against a bounded ohms range, a target value, or a continuity check IS
    // the failure — the open element, the open thermal fuse, the open sensor.
    // Against a voltage or a temperature it says nothing, and against an ohms
    // floor alone it genuinely satisfies the bound.
    return ((e.unit === 'ohms' || e.unit === 'kohm') && (e.max != null || e.value != null))
      ? { verdict: 'fail', text: 'FAIL (OL — open circuit)' }
      : { verdict: 'ol', text: 'OL (open circuit)' };
  }
  let v = Number(raw);
  // isFinite, not isNaN: "1e999" parses to Infinity and would sail through
  // every comparison as an ordinary number.
  if (!Number.isFinite(v)) return { verdict: null, text: '' };

  // Only f<->c and Ω<->kΩ can still differ here — every other mismatch already returned
  // above. Converting the reading (not the spec) keeps the bounds in the units
  // the manual quoted.
  if (declared !== e.unit) v = convertReading(v, declared, e.unit);
  // A minus sign on a DC reading only means the leads are reversed; the
  // magnitude is the reading (training: AO&R 04). Judged signed, -12.6 VDC on
  // a 12 V fan supply read as "no supply" and could end in a condemned board.
  // A spec that is itself negative (rare) is judged signed.
  if (e.unit === 'vdc' && ![e.min, e.max, e.value].some((b) => b != null && b < 0)) v = Math.abs(v);

  let ok;
  if (e.min != null || e.max != null) {
    // Signed comparison, deliberately. [expect unit=f min=-5 max=5] is a real
    // freezer spec from production; an abs() here would break every
    // below-zero diagnosis.
    ok = (e.min == null || v >= e.min) && (e.max == null || v <= e.max);
  } else if (e.value != null) {
    // abs() ONLY on the zero target: a continuity check reading -1000 Ω is an
    // instrument or probe problem, not a pass. Kept inside this branch so a
    // temperature value=0 still accepts -1 °F. A percentage of zero is zero,
    // which is why the zero case needs its own absolute window at all.
    ok = e.value === 0
      ? Math.abs(v) <= ZERO_TARGET_ABS
      : Math.abs(v - e.value) <= Math.abs(e.value) * effectiveTol(e) / 100;
  } else ok = null;
  if (ok == null) return { verdict: null, text: '' };
  return { verdict: ok ? 'pass' : 'fail', text: ok ? 'PASS' : 'FAIL' };
}

/**
 * The sentence a structured reading becomes when it is sent, so the AI sees
 * the value, the unit and our local judgement in one place.
 *
 * Lives here rather than inline in the composer so a regression can assert
 * that a mismatch sentence contains no PASS or FAIL token anywhere.
 */
export function buildReadingSentence({ raw, readingUnit, expect, judgement }) {
  const value = /^ol$/i.test(String(raw).trim()) ? 'OL' : String(raw).trim();
  const unitLabel = READING_UNITS[readingUnit]?.label || '';
  const head = `Reading: ${value}${unitLabel ? ' ' + unitLabel : ''}`;
  // 'ol' is a neutral observation and the value already says OL — appending
  // "— OL (open circuit)" produced "Reading: OL Ω — OL (open circuit)".
  const verdict = judgement?.verdict && judgement.verdict !== 'ol' && judgement.text
    ? ` — ${judgement.text}`
    : '';
  // "typical" tells the model this PASS/FAIL was judged against general
  // guidance, not this unit's spec (D-REV16).
  const target = expect ? ` (${expect.src === 'typical' ? 'typical' : 'expected'} ${describeExpect(expect)})` : '';
  return `${head}${verdict}${target}.`;
}

// ─── Rendering ──────────────────────────────────────────────────────────────

/**
 * Split a turn into the prose a human reads and the visuals the app renders.
 *
 * Returns { textOnly, visuals, expect, expects }.
 *   expect  — the single [expect …] to drive the reading row, or null.
 *   expects — every marker found. Two of them means no single target exists;
 *             `expect` is null and the tech answers in prose. The worker
 *             already strips that case at write time, but turns stored before
 *             the guard worked are still in D1 and must not fabricate a
 *             verdict when a session is rehydrated.
 */
export function parseTurnContent(text) {
  if (!text) return { textOnly: '', visuals: [], expect: null, expects: [] };
  const visuals = [];
  const expects = [];
  // Replace every match with a single space so we don't fuse adjacent words
  // and so the prose still flows naturally where the marker was.
  const textOnly = text.replace(MARKER_RE, (match, docFull, docId, pageNum, svgFull, svgInner, expectFull, expectAttrs) => {
    if (docFull) {
      visuals.push({
        type: 'doc',
        docId: Number(docId),
        page: pageNum != null ? Number(pageNum) : null,
        // Stable key for React even if the same doc is cited twice.
        key: `doc-${docId}-${pageNum ?? 'all'}-${visuals.length}`,
      });
    } else if (svgFull) {
      visuals.push({
        type: 'svg',
        markup: svgInner,
        key: `svg-${visuals.length}`,
      });
    } else if (match.startsWith('[expect')) {
      const p = parseExpect(expectAttrs);
      if (p) expects.push(p);
    }
    return ' ';
  })
    // Anything the model started and never closed. Runs after MARKER_RE so a
    // well-formed marker is already gone and cannot be half-matched here.
    .replace(UNTERMINATED_RE, ' ');

  // Tidy up whitespace introduced by replacement. The model also drifts into
  // markdown despite the FORMAT rule (session 20 rendered literal ** around
  // words); the transcript renders plain text, so strip bold markers and
  // heading hashes rather than show them.
  return {
    textOnly: textOnly
      .replace(/\*\*(.+?)\*\*/g, '$1')
      .replace(/^#{1,6}\s+/gm, '')
      .replace(/[ \t]+/g, ' ')
      .replace(/ ?\n ?/g, '\n')
      .trim(),
    visuals,
    expect: expects.length === 1 ? expects[0] : null,
    expects,
  };
}
